// Phone-only building blocks for the daily admin lists (Printing, AMEEX,
// Orders): a bulk-action bar pinned to the bottom of the screen, and a
// checkbox with a thumb-sized tap area.
//
// Why a pinned bar: on a phone the selection is made while scrolling down a
// list of cards, so an action bar at the top of the page has scrolled away by
// the time it is needed. The bar sits ABOVE the shell's bottom tab bar
// (`--adm-bottom-h`, which already includes the bottom safe area) and adds the
// safe area itself only when no tab bar covers it.
//
// Desktop never sees either piece: the bar renders nothing ≥768px, where each
// page keeps its existing inline bar.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useIsPhone } from '@/lib/phoneViewport';

const BAR_FALLBACK_PX = 96;

export function PhoneActionBar({
  label, onClear, clearLabel = 'Effacer', children,
}: {
  /** What is selected, e.g. « 3 commandes · 1 240 MAD ». */
  label: ReactNode;
  /** Shown as a quiet « Effacer » button when given. */
  onClear?: () => void;
  clearLabel?: string;
  /** The actions — each should call the SAME handler the desktop bar uses. */
  children: ReactNode;
}) {
  const phone = useIsPhone();
  const barRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(BAR_FALLBACK_PX);

  // The spacer tracks the bar's real height (it grows when actions wrap).
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () => { if (el.offsetHeight) setHeight(el.offsetHeight); };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [phone]);

  if (!phone) return null;

  return (
    <>
      <div data-phone-action-bar-spacer aria-hidden style={{ height }} />
      {/* Portalled: a route transition may transform the page, and a transformed
          ancestor would turn `position: fixed` into « fixed to the page ». */}
      {createPortal(
        <div
          ref={barRef}
          data-phone-action-bar
          role="toolbar"
          aria-label={typeof label === 'string' ? label : undefined}
          className="z-40 border-t border-brand-200/70 dark:border-adm-hairline bg-white/95 backdrop-blur-md shadow-[0_-8px_24px_rgba(46,36,24,0.08)] motion-safe:animate-[adm-rise_220ms_cubic-bezier(0.2,0.7,0.3,1)_both]"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 'var(--adm-bottom-h, 0px)',
            paddingTop: 10,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 'calc(10px + max(0px, env(safe-area-inset-bottom, 0px) - var(--adm-bottom-h, 0px)))',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <p className="flex-1 min-w-0 text-[15px] font-semibold text-text-primary truncate">{label}</p>
            {onClear && (
              <button
                type="button"
                data-clear
                onClick={onClear}
                className="min-h-11 px-3 -mr-2 rounded-lg text-sm font-medium text-text-secondary active:bg-brand-50"
              >
                {clearLabel}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 [&>*:only-child]:col-span-2 [&>*]:min-h-11 [&>*]:w-full [&>*]:px-2 [&>*]:leading-tight">
            {children}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * A checkbox whose tap area is a full 44×44 px square. Clicks never bubble to
 * the card/row around it, so ticking never opens the order.
 */
export function SelectTick({
  checked, onChange, label, className = '',
}: { checked: boolean; onChange: () => void; label: string; className?: string }) {
  return (
    <label
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center w-11 h-11 shrink-0 rounded-lg cursor-pointer active:bg-brand-50 ${className}`}
    >
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={onChange}
        className="w-5 h-5 rounded border-gray-300 accent-[#C9A04C] cursor-pointer"
      />
    </label>
  );
}
